import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useEffect, useRef } from 'react'
import useToggle from './useToggle'

/**
 * Re-implementation of `useState`, except if the input value changes then the state
 * will be reset to that value. This is useful for when you want to be able to update
 * the state within a component, but also respond when the parent passes in a new value.
 *
 * You could approximate this behaviour with `useEffect` like so:
 *
 * ```tsx
 * const [value, setValue] = useState(inputValue)
 * useEffect(() => {
 *   setValue(inputValue)
 * }, [inputValue])
 * ```
 *
 * But that would cause the component to re-render twice when the parent updates the value.
 * This hook will not cause superfluous re-renders.
 */
export default function useSyncedState<T>(inputValue: T, disableSync = false): [T, Dispatch<SetStateAction<T>>] {
  const value = useRef<T>(inputValue)
  const lastInputValue = useRef<T>(inputValue)
  const { toggle: triggerRerender } = useToggle()

  const shouldSync = !disableSync && inputValue !== lastInputValue.current

  useEffect(() => {
    if (shouldSync) {
      value.current = inputValue
      lastInputValue.current = inputValue
    }
  })

  const setState = useCallback<Dispatch<SetStateAction<T>>>((arg) => {
    value.current = typeof arg === 'function' ? (arg as (prev: T) => T)(value.current) : arg
    triggerRerender()
  }, [triggerRerender])

  if (shouldSync) {
    return [inputValue, setState]
  } else {
    return [value.current, setState]
  }
}
